<template>
  <div
    ref="productsSliderWrapper"
    class="scroll-section mt-50 lg:-mb-40"
  >
    <div class="scroll-pin">
      <div class="flex flex-col gap-y-6 lg:gap-y-10 items-center mb-20 -mt-40">
        <a
          href="#"
          class="w-fit px-4 py-2 max-md:mb-3 rounded-full bg-white drop-shadow-md flex items-center gap-x-2 text-sm font-instrumentSans"
        >
          <span>🔥</span>
          <span class="font-normal">
            <span class="font-semibold">{{
              t("pages.home.products.pill.label")
            }}</span>
            <i class="bi bi-dot"></i>
            <span>{{ t("pages.home.products.pill.text") }}</span>
          </span>
        </a>

        <h1
          class="text-[26px] leading-[36px] lg:text-5xl lg:leading-[56px] font-bold text-dark text-center font-markPro max-w-[850px] tracking-[-0.06rem] lg:tracking-[-0.08rem]"
        >
          {{ t("pages.home.products.heading") }}
        </h1>

        <div class="flex flex-col gap-y-4">
          <div class="flex flex-col text-center lg:text-lg font-semibold">
            <p class="text-lighter">
              {{ t("pages.home.products.stop_overspending") }}
            </p>
            <p class="text-dark">
              {{ t("pages.home.products.productivity_increase") }}
            </p>
          </div>
        </div>

        <div class="flex justify-center items-center">
          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-light text-sm"
          >
            {{ t("pages.home.products.start_for_free") }}
          </a>
        </div>
      </div>

      <div ref="productsSlider" class="scroll-track">
        <div
          v-for="(card, i) in cards"
          :key="card.key"
          class="card"
          :style="{ '--i': i, '--n': cards.length }">
          <img class="card-img" :src="card.image" :alt="card.imgAlt" />
          <div class="card-overlay"></div>
          <div class="card-body">
            <h4 class="card-title">
              {{ t('pages.home.products.cards.bistroo') }} {{ card.title }}
            </h4>
            <RouterLink :to="card.to" class="btn btn-light w-fit">
              {{ card.action }}
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { clamp } from '@/utils/clamp'
import { publicPath } from '@/utils/publicPath'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'

const { t } = useI18n()

const productsSliderWrapper = ref<HTMLDivElement | undefined>()

const cards: {
  key: string
  image: string
  to: RouteLocationRaw
  imgAlt: string
  title: string
  action: string
}[] = [
  {
    key: 'page',
    image: publicPath('/images/img-slider-website.webp'),
    to: { name: 'products.page' },
    imgAlt: t('pages.home.products.cards.page.img_alt'),
    title: t('pages.home.products.cards.page.title'),
    action: t('pages.home.products.cards.page.action'),
  },
  {
    key: 'webshop',
    image: publicPath('/images/img-slider-webshop.webp'),
    to: { name: 'products.webshop' },
    imgAlt: t('pages.home.products.cards.webshop.img_alt'),
    title: t('pages.home.products.cards.webshop.title'),
    action: t('pages.home.products.cards.webshop.action'),
  },
  {
    key: 'pos',
    image: publicPath('/images/img-slider-pos.webp'),
    to: { name: 'products.pos' },
    imgAlt: t('pages.home.products.cards.pos.img_alt'),
    title: t('pages.home.products.cards.pos.title'),
    action: t('pages.home.products.cards.pos.action'),
  },
  {
    key: 'kiosk',
    image: publicPath('/images/img-slider-kiosk.webp'),
    to: { name: 'products.kiosk' },
    imgAlt: t('pages.home.products.cards.kiosk.img_alt'),
    title: t('pages.home.products.cards.kiosk.title'),
    action: t('pages.home.products.cards.kiosk.action'),
  },
  {
    key: 'qr',
    image: publicPath('/images/img-slider-qr.webp'),
    to: { name: 'products.qr' },
    imgAlt: t('pages.home.products.cards.qr.img_alt'),
    title: t('pages.home.products.cards.qr.title'),
    action: t('pages.home.products.cards.qr.action'),
  },
  {
    key: 'posdevice',
    image: publicPath('/images/img-slider-posdevice.webp'),
    to: { name: 'products.posdevice' },
    imgAlt: t('pages.home.products.cards.posdevice.img_alt'),
    title: t('pages.home.products.cards.posdevice.title'),
    action: t('pages.home.products.cards.posdevice.action'),
  },
  {
    key: 'manager',
    image: publicPath('/images/img-slider-manager.webp'),
    to: { name: 'products.manager' },
    imgAlt: t('pages.home.products.cards.manager.img_alt'),
    title: t('pages.home.products.cards.manager.title'),
    action: t('pages.home.products.cards.manager.action'),
  },
]

const initializeTimelinePolyfill = () => {
  let cleanup: (() => void) | undefined

  onMounted(() => {
    if (CSS.supports('animation-timeline: view()')) {
      return
    }

    const section = productsSliderWrapper.value
    if (!section) {
      return
    }

    const onScroll = () => {
      const rect = section.getBoundingClientRect()
      const total = rect.height - window.innerHeight

      section.style.setProperty('--progress', String(clamp(-rect.top / total, 0, 1)))
    }

    document.addEventListener('scroll', onScroll, { passive: true })

    onScroll()

    cleanup = () => document.removeEventListener('scroll', onScroll)
  })

  onBeforeUnmount(() => cleanup?.())
}

initializeTimelinePolyfill()
</script>

<style scoped>
@reference '@/assets/css/main.css';

.card {
  @apply snap-start shrink-0 w-[84%] mx-8 lg:mx-0 mb-10 lg:w-[380px] h-full min-h-[400px] lg:min-h-[480px] relative rounded-2xl overflow-hidden;

  transform-origin: center center;

  --slice: calc(100% / var(--n));
  /* each card peaks at its center-crossing point */
  animation-range: contain calc((var(--i) - 1) * var(--slice)) contain
    calc((var(--i) + 1) * var(--slice));

  .card-img {
    @apply absolute inset-0 w-full h-full object-cover transition-transform duration-500;
  }

  .product-slide-card:hover .card-img {
    @apply scale-105;
  }

  .card-overlay {
    @apply absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent;
  }

  .card-body {
    @apply absolute bottom-0 left-0 right-0 p-8 py-13 flex flex-col gap-y-6 items-center;
  }

  .card-title {
    @apply font-markPro text-[30px] font-bold text-white leading-tight text-center;
    text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  }
}

/** MOBILE */
@media (max-width: theme(--breakpoint-lg)) {
  .scroll-section {
    @apply flex justify-center;

    .scroll-pin {
      .scroll-track {
        @apply relative flex flex-col gap-y-4;

        .card {
          @apply min-h-[450px];
        }
      }
    }
  }
}

/** DESKTOP */
@media (min-width: theme(--breakpoint-lg)) {
  .scroll-section {
    --container-margin: 12vw;

    height: 300vh;
    view-timeline-name: --cards;
    view-timeline-axis: block;

    .scroll-pin {
      @apply sticky top-0 h-screen w-screen flex flex-col;

      .scroll-track {
        @apply flex w-max;
        margin-left: var(--container-margin);

        /* Modern browser support */
        @supports (animation-timeline: view()) {
          animation: slide-x linear both;
          animation-timeline: --cards;
          animation-range: contain 0% contain 100%;

          .card {
            transform-origin: center center;

            animation: card-scale linear both;
            animation-timeline: --cards; /* same timeline as the track */

            --slice: calc(100% / var(--n));
            /* each card peaks at its center-crossing point */
            animation-range: contain calc((var(--i) - 1) * var(--slice)) contain
              calc((var(--i) + 1) * var(--slice));
          }
        }

        /* No support for timelines */
        @supports not (animation-timeline: view()) {
          gap: 2rem;
          translate: calc(var(--progress) * (-100% + 75vw)) 0;

          scroll-snap-type: y proximity;
          .card {
            scroll-snap-align: center;
          }
        }
      }
    }
  }
}

@keyframes card-scale {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes slide-x {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100% + 100vw - var(--container-margin) * 2));
  }
}
</style>
